import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getTradeBarOrders,
  TradeBarLayout,
} from "common/NewLook/tradeBarLayout.ts";
import {
  getDefaultTradeBarTarget,
  getTradeBarTarget,
  TradeBarTarget,
} from "common/NewLook/tradeBarHover.ts";
import { LevelsLadderModel } from "common/NewLook/priceLadder.ts";

interface TradeBarSheetState {
  open: boolean;
  target: TradeBarTarget;
}

type BarPointerEvent = React.PointerEvent<HTMLDivElement>;
type BarMouseEvent = React.MouseEvent<HTMLDivElement>;

const OPEN_DELAY = 150;
const CLOSE_DELAY = 100;
const SWAP_WINDOW = 300;
const SNAP = { mouse: 8, touch: 16 };
const ACTIVATION_KEYS = new Set(["Enter", " "]);

let lastClosedAt = 0;

const isSameTarget = (first?: TradeBarTarget, second?: TradeBarTarget) =>
  first?.kind === second?.kind && first?.index === second?.index;

const useHoverIntent = () => {
  const [hover, setHover] = useState<TradeBarTarget>();
  const isOpenRef = useRef(false);
  const pendingRef = useRef<TradeBarTarget | undefined>(undefined);
  const openTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);
  const reveal = useCallback(() => {
    openTimer.current = undefined;
    isOpenRef.current = true;
    setHover((current) =>
      isSameTarget(current, pendingRef.current) ? current : pendingRef.current
    );
  }, []);
  const show = useCallback(
    (target: TradeBarTarget) => {
      window.clearTimeout(closeTimer.current);
      pendingRef.current = target;
      if (isOpenRef.current || Date.now() - lastClosedAt < SWAP_WINDOW)
        return reveal();
      if (openTimer.current === undefined)
        openTimer.current = window.setTimeout(reveal, OPEN_DELAY);
    },
    [reveal]
  );
  const hide = useHoverHide(isOpenRef, openTimer, closeTimer, setHover);
  useEffect(
    () => () => {
      window.clearTimeout(openTimer.current);
      window.clearTimeout(closeTimer.current);
    },
    []
  );
  useCloseOnScroll(!!hover, hide);
  return { hover, show, hide };
};

const useHoverHide = (
  isOpenRef: React.RefObject<boolean>,
  openTimer: React.RefObject<number | undefined>,
  closeTimer: React.RefObject<number | undefined>,
  setHover: (target?: TradeBarTarget) => void
) =>
  useCallback(
    (immediate = false) => {
      window.clearTimeout(openTimer.current);
      openTimer.current = undefined;
      window.clearTimeout(closeTimer.current);
      const close = () => {
        if (isOpenRef.current) lastClosedAt = Date.now();
        isOpenRef.current = false;
        setHover(undefined);
      };
      if (immediate) return close();
      closeTimer.current = window.setTimeout(close, CLOSE_DELAY);
    },
    [isOpenRef, openTimer, closeTimer, setHover]
  );

const useCloseOnScroll = (
  isOpen: boolean,
  hide: (immediate?: boolean) => void
) =>
  useEffect(() => {
    if (!isOpen) return;
    const onScroll = () => hide(true);
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [isOpen, hide]);

const getKeyboardTarget = (model: LevelsLadderModel): TradeBarTarget => {
  const orders = getTradeBarOrders(model);
  return getDefaultTradeBarTarget(
    orders,
    orders.entries.length ? "entry" : "price"
  );
};

const useTradeBarInteraction = (
  model: LevelsLadderModel,
  layout: TradeBarLayout | undefined,
  onSelect?: (target: TradeBarTarget) => void
) => {
  const hoverIntent = useHoverIntent();
  const [sheet, setSheet] = useState<TradeBarSheetState>();
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const pointerTypeRef = useRef("mouse");
  const isDraggingRef = useRef(false);
  const targetAt = (event: BarMouseEvent) => {
    if (!layout) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const snap = pointerTypeRef.current === "mouse" ? SNAP.mouse : SNAP.touch;
    return getTradeBarTarget(layout.points, event.clientX - rect.left, snap);
  };
  const openSheet = (target?: TradeBarTarget) =>
    target && setSheet({ open: true, target });
  const select = (event: BarPointerEvent) => {
    const target = targetAt(event);
    if (target) onSelect?.(target);
  };
  const onPointerDown = (event: BarPointerEvent) => {
    pointerTypeRef.current = event.pointerType;
    if (!onSelect) return;
    isDraggingRef.current = true;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    select(event);
  };
  const onPointerMove = (event: BarPointerEvent) => {
    if (onSelect) return isDraggingRef.current && select(event);
    if (event.pointerType !== "mouse") return;
    pointerTypeRef.current = "mouse";
    anchorRef.current = event.currentTarget;
    const target = targetAt(event);
    if (target) hoverIntent.show(target);
  };
  const onClick = (event: BarMouseEvent) => {
    if (onSelect || pointerTypeRef.current === "mouse") return;
    event.stopPropagation();
    openSheet(targetAt(event));
  };
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (onSelect || !layout || !ACTIVATION_KEYS.has(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    openSheet(getKeyboardTarget(model));
  };
  const handlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp: () => (isDraggingRef.current = false),
    onPointerCancel: () => (isDraggingRef.current = false),
    onPointerLeave: () => !onSelect && hoverIntent.hide(),
    onClick,
    onKeyDown,
  };
  return {
    handlers,
    hover: hoverIntent.hover,
    anchor: anchorRef.current,
    sheet,
    setSheetTarget: (target: TradeBarTarget) =>
      setSheet((current) => current && { ...current, target }),
    closeSheet: () =>
      setSheet((current) => current && { ...current, open: false }),
  };
};

export default useTradeBarInteraction;
